import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Save } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ParticipantPicker from "@/components/ParticipantPicker";

export default function EditTaskModal({ open, onOpenChange, task, onSaved }) {
  const { user: me } = useAuth();
  const [form, setForm] = useState({ title: "", description: "", due_date: "", assignee_id: "", participant_ids: [] });
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && task) {
      setForm({
        title: task.title || "",
        description: task.description || "",
        due_date: task.due_date || "",
        assignee_id: task.assignee_id || me?.id || "",
        participant_ids: task.participant_ids || [],
      });
      api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    }
  }, [open, task, me?.id]);

  const save = async () => {
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/tasks/${task.id}`, {
        title: form.title.trim(),
        description: form.description,
        due_date: form.due_date || null,
        assignee_id: form.assignee_id || me?.id,
        participant_ids: form.participant_ids,
      });
      toast.success("Task updated");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  if (!task) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-task-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Edit task</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Title</label>
            <input
              className="tg-input mt-1"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="edit-task-title"
              autoFocus
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Description</label>
            <textarea
              className="tg-input mt-1 min-h-[100px] resize-none"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="edit-task-description"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Due date</label>
              <input
                type="date"
                className="tg-input mt-1"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="edit-task-due"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Assignee</label>
              <select
                className="tg-input mt-1 text-xs"
                value={form.assignee_id}
                onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}
                data-testid="edit-task-assignee"
              >
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.id === me?.id ? `${u.name} (me)` : u.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Tag teammates</label>
            <div className="mt-1">
              <ParticipantPicker
                value={form.participant_ids}
                onChange={(ids) => setForm({ ...form, participant_ids: ids })}
                excludeIds={form.assignee_id ? [form.assignee_id] : []}
                placeholder="Tag collaborators — they'll see this task & the thread"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={() => onOpenChange(false)} className="tg-btn-secondary" data-testid="edit-task-cancel">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy || !form.title.trim()}
            className="tg-btn-primary disabled:opacity-50"
            data-testid="edit-task-save"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
